import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  ArrayField,
  SingleFieldList,
  ChipField
} from 'react-admin';
import { EntityMetadata, EntityFieldMetadata } from '../../services/types/entity-discovery.types';

interface DynamicEntityListProps {
  entityMetadata: EntityMetadata;
}

export const DynamicEntityList: React.FC<DynamicEntityListProps> = ({ entityMetadata }) => {
  const renderField = (field: EntityFieldMetadata) => {
    // Skip internal fields in list view
    if (field.name === '_id' || field.name === 'createdAt' || field.name === 'updatedAt') {
      return null;
    }

    const commonProps = {
      key: field.name,
      source: field.name,
      label: formatFieldLabel(field.name)
    };

    // Handle arrays
    if (field.isArray) {
      return (
        <ArrayField {...commonProps}>
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ArrayField>
      );
    }

    // Handle references
    if (field.ref) {
      return (
        <ReferenceField 
          {...commonProps} 
          reference={field.ref.toLowerCase()}
        >
          <TextField source="name" />
        </ReferenceField>
      );
    }

    // Handle different field types
    switch (field.type.toLowerCase()) {
      case 'number':
      case 'decimal128':
        return <NumberField {...commonProps} />;
      
      case 'boolean':
        return <BooleanField {...commonProps} />;
      
      case 'date':
        return <DateField {...commonProps} />;
      
      case 'string':
      default:
        return <TextField {...commonProps} />;
    }
  };

  // Get the most important fields for list view (limit to prevent overcrowding)
  const getListFields = () => {
    const fields = entityMetadata.fields.filter(field => 
      !['_id', 'createdAt', 'updatedAt', 'context'].includes(field.name)
    );

    // Prioritize certain field names for display
    const priorityFields = ['name', 'title', 'firstName', 'lastName', 'email', 'phone', 'status'];
    const prioritized = fields.filter(field => 
      priorityFields.some(priority => field.name.toLowerCase().includes(priority.toLowerCase()))
    );

    const others = fields.filter(field => 
      !priorityFields.some(priority => field.name.toLowerCase().includes(priority.toLowerCase()))
    );

    // Take up to 5 fields total, prioritizing important ones
    return [...prioritized.slice(0, 3), ...others.slice(0, 2)];
  };

  const fieldsToShow = getListFields();

  return (
    <List title={`${entityMetadata.name} List`}>
      <Datagrid rowClick="edit">
        {fieldsToShow.map(field => renderField(field))}
      </Datagrid>
    </List>
  );
};

/**
 * Convert field name from camelCase to human readable format
 */
function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}
